import { useId } from 'react';
import { SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID } from './slideLevelClassificationModel';
import {
    truncateWithEllipsis,
    compactFreeTextOptionHeadline,
} from './HybridClassSelect';
import styles from './ChoiceList.module.css';

/**
 * Always-visible choice list for QuestionsPanel (when selectable count <= SLC_MAX_INLINE_CHOICES).
 * Not used in the viewer top bar; see QuestionAnswerControls layout="topBar".
 */
export function InlineChoiceList({
    categories = [],
    value = '',
    categoryId = '',
    freeTextAnswer = '',
    freeTextOptionLabel = 'Other (free text)',
    includeFreeText = false,
    disabled = false,
    labelledById,
    onCommitSelection,
}) {
    const listId = useId();

    const freeSelected = String(categoryId) === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
    const freePreviewRaw = String(freeTextAnswer ?? '').trim();
    const freePreview =
        freePreviewRaw.length > 0
            ? truncateWithEllipsis(freePreviewRaw, 40)
            : null;
    const freeRowHeadline = compactFreeTextOptionHeadline(freeTextOptionLabel);
    const freeTextAriaLabel = freePreviewRaw
        ? `${freeTextOptionLabel}. Current answer: ${truncateWithEllipsis(freePreviewRaw, 72)}. Opens text editor.`
        : `${freeTextOptionLabel}. Opens text editor.`;

    function pick(v) {
        if (!disabled) onCommitSelection(v);
    }

    const list = (
        <ul
            id={listId}
            className={styles.listVertical}
            role="listbox"
            aria-labelledby={labelledById}
        >
            <li role="presentation">
                <button
                    type="button"
                    role="option"
                    aria-selected={value === ''}
                    disabled={disabled}
                    className={[
                        styles.option,
                        value === '' ? styles.optionSelected : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => pick('')}
                >
                    — Select —
                </button>
            </li>
            {categories.map((c) => {
                const id = String(c.id);
                const label = c.label?.trim() ? c.label : id;
                const selected = value === id;
                return (
                    <li key={c.id} role="presentation">
                        <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            disabled={disabled}
                            className={[
                                styles.option,
                                selected ? styles.optionSelected : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => pick(id)}
                        >
                            {label}
                        </button>
                    </li>
                );
            })}
            {includeFreeText ? (
                <li role="presentation">
                    <button
                        type="button"
                        className={[
                            styles.option,
                            styles.freeTextOption,
                            freeSelected ? styles.optionSelected : '',
                        ].filter(Boolean).join(' ')}
                        role="option"
                        aria-selected={freeSelected}
                        disabled={disabled}
                        aria-label={freeTextAriaLabel}
                        onClick={() => pick(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID)}
                    >
                        {freePreview ? (
                            <span className={styles.optionLabel}>
                                {`${freeRowHeadline}: ${freePreview}`}
                            </span>
                        ) : (
                            <span className={styles.optionLabel}>{freeTextOptionLabel}</span>
                        )}
                    </button>
                </li>
            ) : null}
        </ul>
    );

    return (
        <div className={styles.listPanel}>
            {list}
        </div>
    );
}
